import { useEffect, useState } from 'react';
import { LoadingSpinner } from 'components/ui/LoadingSpinner';
import { deleteCard, getCreditCardList, getPaymentMethodReserve } from 'apis/payment';
import { S3_BASE_URL } from 'constants/static';
import { useTranslation } from 'i18n';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { cardListState, isLoginFormOpenState } from 'store/atoms';
import { useSearchParams } from 'react-router-dom';
import {
  PaymentContainer,
  CardContainer,
  CardOptionLeftContainer,
  CardOptionContainer,
  RadioButton,
  CardOption,
  CardNumber,
  CardType,
  AddCardButton,
  DeleteIcon,
  LoadingContainer,
  NoDataCardList,
  NoDataText,
  NoDataButtonContainer,
} from './CardManagement.styles';

export function CardManagement({ management }: { management?: boolean }) {
  const { t } = useTranslation();
  const [cardList, setCardList] = useRecoilState<any>(cardListState);
  const setIsLoginFormOpen = useSetRecoilState(isLoginFormOpenState);
  const [selectedCard, setSelectedCard] = useState('');
  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');

  const handleAddCard = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) {
      setIsLoginFormOpen(true);
    }
    try {
      const res: any = await getPaymentMethodReserve();
      window.open(res.data, '_blank');
    } catch {
      console.log('error');
    }
  };

  const getCardList = async () => {
    try {
      setLoading(true);
      const cards = await getCreditCardList();
      setCardList(cards);
    } catch (error) {
      console.error('Failed to fetch card list:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async (creditCardId: any) => {
    await deleteCard(creditCardId);
    getCardList();
  };

  useEffect(() => {
    if (localStorage.getItem('defaultCardId')) {
      setSelectedCard(localStorage.getItem('defaultCardId') ?? '');
    } else {
      setSelectedCard(cardList?.length > 0 ? (cardList[0]?.userCreditCardId ?? null) : null);
    }

    if (cardList.length === 0) {
      localStorage.removeItem('defaultCardId');
    }
  }, [cardList]);

  useEffect(() => {
    //defaultCardId를 localStorage에 저장
    if (selectedCard) {
      localStorage.setItem('defaultCardId', selectedCard);
    }
  }, [selectedCard]);
  //만약 페이지가 포커스를 잃었다가 다시 포커스를 잡으면 카드 리스트를 다시 불러오기
  useEffect(() => {
    const handleFocus = () => {
      getCardList();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  return (
    <PaymentContainer>
      {loading ? (
        <LoadingContainer>
          <LoadingSpinner size={120} />
        </LoadingContainer>
      ) : cardList?.length > 0 ? (
        <CardContainer>
          {cardList?.map((card: any) => (
            <CardOption
              key={card.userCreditCardId}
              $isSelected={selectedCard === card.userCreditCardId}
              $management={management}
              onClick={() => setSelectedCard(card.userCreditCardId)}
            >
              <CardOptionLeftContainer>
                <CardOptionContainer>
                  <CardNumber>**** **** **** {card.userCreditCardNumber}</CardNumber>
                  <CardType>{card.userCreditCardCardCompany}</CardType>
                </CardOptionContainer>
              </CardOptionLeftContainer>
              {management ? (
                <div
                  onClick={(e: any) => {
                    e.stopPropagation(); // 이벤트 버블링 방지
                    handleDeleteCard(card.userCreditCardId);
                  }}
                >
                  <DeleteIcon src={`${S3_BASE_URL}/card_delete_icon.png`} alt="Delete" />
                </div>
              ) : (
                <RadioButton
                  type="radio"
                  name="card"
                  checked={selectedCard === card.userCreditCardId}
                  onChange={() => setSelectedCard(card.userCreditCardId)}
                />
              )}
            </CardOption>
          ))}
        </CardContainer>
      ) : (
        <NoDataCardList $type={type}>
          <NoDataText>{t('card.no_data')}</NoDataText>
          <NoDataButtonContainer>
            <AddCardButton
              text={t('card.cta.add_new')}
              size="small"
              variant="green"
              onClick={async () => await handleAddCard()}
            />
          </NoDataButtonContainer>
        </NoDataCardList>
      )}
    </PaymentContainer>
  );
}
